import moment from "moment";
import type { VisitorListItem } from "src/api/visitor/visitor-types";
import type { ExcelColumn } from "src/pages/mypage/contract/hooks/useExcelDownload";
import { convertKoreanPhoneNumber } from "src/utils/common-util";
import { formatVisitApplyNumber, getInvitationChannelLabel, getVisitorCompanyName } from "./visitorHistory";
import {
  getVisitorApprovalStatusLabel,
  getVisitorHasAccessLog,
  getVisitorVisitPurposeLabel,
  getVisitorVisitStatusLabel,
} from "./visitorHistoryStatus";

const emptyIfDash = (value: string) => (value === "-" ? "" : value);

const formatExcelDateTime = (date?: string) => {
  if (!date) return "";

  const targetDate = moment(date);

  return targetDate.isValid() ? targetDate.format("YYYY-MM-DD HH:mm:ss") : "";
};

const makeVisitorHistoryExcelFileName = (contractName?: string) => {
  const fileNamePrefix = contractName
    ?.replace(/[\\/:*?"<>|]/g, "_")
    .trim()
    .slice(0, 10);
  const downloadedAt = moment().format("YYYYMMDDHHmm");

  return fileNamePrefix ? `${fileNamePrefix}_Visitor_Invitation_List_${downloadedAt}.xlsx` : `Visitor_Invitation_List_${downloadedAt}.xlsx`;
};

const visitorHistoryExcelColumns: ExcelColumn<VisitorListItem>[] = [
  { header: "방문신청번호", value: (row) => formatVisitApplyNumber(row.visitApplyNumber) },
  {
    header: "1단계 승인상태",
    value: (row) => getVisitorApprovalStatusLabel({ approvalStatus: row.memberApprovalStatus }),
  },
  {
    header: "2단계 승인상태",
    value: (row) => getVisitorApprovalStatusLabel({ approvalStatus: row.approvalStatus }),
  },
  {
    header: "방문상태",
    value: (row) => getVisitorVisitStatusLabel(row.visitStatus, { hasAccessLog: getVisitorHasAccessLog(row), isDeleted: row.isDeleted }),
  },
  { header: "방문자명", value: (row) => row.visitorName || "" },
  { header: "방문자 휴대폰번호", value: (row) => (row.visitorMobileNumber ? convertKoreanPhoneNumber(row.visitorMobileNumber) : "") },
  { header: "방문자 이메일", value: (row) => row.visitorEmail || "" },
  { header: "소속 회사", value: (row) => emptyIfDash(getVisitorCompanyName(row)) },
  { header: "방문 목적", value: (row) => emptyIfDash(getVisitorVisitPurposeLabel(row.visitPurpose, row.visitPurposeDescription)) },
  { header: "방문시작일시", value: (row) => formatExcelDateTime(row.visitStartTime) },
  { header: "방문종료일시", value: (row) => formatExcelDateTime(row.visitEndTime) },
  { header: "주차 차량 번호", value: (row) => row.visitorCarNumber || "" },
  { header: "초대 경로", value: (row) => emptyIfDash(getInvitationChannelLabel(row.invitationChannel)) },
];

export const makeVisitorHistoryExcelData = (rows: VisitorListItem[], contractName?: string) => {
  const fileName = makeVisitorHistoryExcelFileName(contractName || rows[0]?.contractName);

  return {
    addFileNameSuffix: false,
    columns: visitorHistoryExcelColumns,
    fileName,
    rows,
    sheetName: "방문자 초대내역",
    webViewFileName: fileName,
  };
};
